import { brandBlueAlpha } from "@/assets/brand/colors";
import React from "react";
import { Check, type LucideIcon } from "lucide-react";

/**
 * The drafting system shared by every attorney-facing intake screen.
 *
 * The motif is German grid paper: hairline-outlined sheets on a squared canvas,
 * technical typography, and construction lines in the brand navy. Color is
 * reserved for actions, so status is carried by ink rather than by fill.
 *
 * Deliberately not used by the client-facing intake at pages/PublicOutgoingIntake,
 * which keeps its own Typeform voice.
 */

/**
 * Navy for affirmative or complete, graphite for attention needed, quiet for
 * plain metadata. Geometry is shared with the sidebar BETA chip so the voice
 * matches the rest of the app.
 *
 * Amber is the exception and is spent deliberately: it marks work that is going
 * back to the client rather than merely needing a look, which is the one state
 * in these screens a reader must not miss. Drawn as a tinted hairline sheet like
 * the others.
 */
export type DraftInk = "navy" | "graphite" | "amber" | "quiet";

export const DRAFT_INK: Record<
  DraftInk,
  { text: string; border: string; fill: string; shadow: string }
> = {
  navy: {
    text: "text-accent-seed",
    border: "border-accent-line",
    fill: "bg-accent-soft",
    shadow: ""
  },
  graphite: {
    text: "text-fg",
    border: "border-line-4",
    fill: "bg-fill-1",
    shadow: ""
  },
  // The warn tone, so the affordance reads as the same orange the rest of the
  // app warns in.
  amber: {
    text: "text-warn",
    border: "border-warn-line",
    fill: "bg-warn-soft",
    shadow: ""
  },
  quiet: {
    text: "text-fg-3",
    border: "border-line-2",
    fill: "bg-elevated",
    shadow: ""
  }
};

/** Text ink for a bare amber numeral, with no sheet behind it. */
export const DRAFT_AMBER_TEXT = "text-warn";

export function draftInkSurface(ink: DraftInk): string {
  const tone = DRAFT_INK[ink];
  return [tone.border, tone.fill, tone.shadow, tone.text]
    .filter(Boolean)
    .join(" ");
}

/** The brand blue the grid, spines and selected hairlines are all drawn in. */
export const DRAFT_LINE = brandBlueAlpha(0.16);
export const DRAFT_LINE_STRONG = brandBlueAlpha(0.35);

/**
 * whitespace-nowrap matters structurally, not just cosmetically: the badge has
 * a fixed height, so a wrapping label would spill out of it. In a table it also
 * keeps the column's min-content width honest, which is what stops a two-word
 * status from being squeezed into the neighbouring cell.
 */
export const DRAFT_BADGE_BASE =
  "inline-flex h-5 shrink-0 items-center justify-center gap-1 whitespace-nowrap " +
  "rounded-xs border border-solid px-2 text-sm";

/** Numerals read as measurements, not prose. */
export const DRAFT_NUMERAL = "font-mono tabular-nums";

/** Micro-label stacked above a value, as on the corner of a drawing. */
export const DRAFT_LABEL =
  "text-xs font-semibold uppercase leading-none tracking-widest text-fg-4";

/** Section heading one step up from DRAFT_LABEL. */
export const DRAFT_SECTION_LABEL =
  "text-xs font-semibold uppercase tracking-widest text-fg-4";

/** A drafted sheet: hairline outline, crisp corner, barely-there lift. */
export const DRAFT_SHEET =
  "rounded-md border border-solid border-line-2 bg-elevated";

/** Sub-sheet nested inside a sheet; no lift of its own. */
export const DRAFT_SUBSHEET = "rounded-xs border border-solid border-line-2";

/** Hairline the sheet takes when it is selected or otherwise active. */
export const DRAFT_SHEET_ACTIVE = "border-accent-line";

/** Clickable sheet, e.g. a choice card or an action row. */
export const DRAFT_ACTION_BASE =
  "group flex w-full items-center justify-between gap-3 rounded-md border border-solid " +
  "border-line-2 bg-elevated px-4 py-3.5 text-left transition-colors duration-100";

export const DRAFT_ACTION_ENABLED = "hover:border-accent-line hover:bg-fill-1";

export const DRAFT_ACTION_DISABLED = "cursor-default opacity-60";

/**
 * Square hairline well holding an icon or a numeral. Replaces the filled pastel
 * circles; pass a size class such as "h-9 w-9" alongside it.
 */
export const DRAFT_WELL =
  "flex flex-none items-center justify-center rounded-xs border border-solid";

/**
 * A selectable row: same geometry as DRAFT_ACTION_BASE, but with the border
 * colour left out so the caller supplies exactly one of the state classes
 * below. Layering a selected hairline on top of a base one is a coin toss,
 * because equal-specificity Tailwind utilities resolve by stylesheet order
 * rather than by the order they appear in the class attribute.
 */
export const DRAFT_CHOICE_ROW =
  "group flex w-full items-center gap-3 rounded-md border border-solid px-4 py-3.5 " +
  "text-left transition-colors duration-100 disabled:cursor-default";

export const DRAFT_CHOICE_ROW_IDLE =
  "border-line-2 bg-elevated hover:border-accent-line hover:bg-fill-1";

/** Selected: navy construction line plus a navy wash. */
export const DRAFT_CHOICE_ROW_SELECTED = "border-accent-line bg-accent-soft";

/** Selected, and consequential: the amber counterpart of the above. */
export const DRAFT_CHOICE_ROW_FLAGGED = "border-warn-line bg-warn-soft";

export const DRAFT_CHOICE_ROW_DISABLED = "border-line-2 bg-elevated opacity-60";

/**
 * Square check box for a chosen row. Square rather than a radio dot so the
 * control matches the squared-paper geometry; the icon is always rendered and
 * only its ink changes, so selecting a row can't shift the layout.
 */
export function DraftChoiceTick({
  selected,
  tone = "navy"
}: {
  selected: boolean;
  tone?: "navy" | "amber";
}) {
  return (
    <span
      className={[
        "flex size-4 flex-none items-center justify-center rounded-xs border border-solid transition-colors",
        selected
          ? tone === "amber"
            ? "border-warn bg-warn text-primary-foreground"
            : "border-accent-seed bg-accent-seed text-primary-foreground"
          : "border-line-4 bg-elevated text-transparent"
      ].join(" ")}
    >
      <Check className="size-3" aria-hidden />
    </span>
  );
}

/** Large choice tile in the wizard: a sheet you can click. */
export const DRAFT_CHOICE_CARD =
  "group flex flex-col gap-5 rounded-md border border-solid border-line-2 bg-elevated p-6 text-left " +
  "transition-colors duration-150 hover:border-accent-line hover:bg-fill-1 " +
  "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/40";

/** Icon well inside a choice tile; takes navy ink as the tile is hovered. */
export const DRAFT_CHOICE_WELL =
  "flex size-10 items-center justify-center rounded-xs border border-solid border-line-2 " +
  "text-fg-2 transition-colors group-hover:border-accent-line " +
  "group-hover:text-accent-seed";

export function DraftBadge({
  ink = "quiet",
  icon,
  className,
  children
}: {
  ink?: DraftInk;
  icon?: LucideIcon;
  className?: string;
  children: React.ReactNode;
}) {
  const Icon = icon;
  return (
    <span
      className={[DRAFT_BADGE_BASE, draftInkSurface(ink), className ?? ""].join(
        " "
      )}
    >
      {Icon ? <Icon className="size-3" aria-hidden /> : null}
      {children}
    </span>
  );
}

/**
 * Square well in the row's ink. Sized by the caller so a 24px step numeral and
 * a 40px status icon can share one treatment.
 */
export function DraftWell({
  ink,
  size = "h-9 w-9",
  className,
  children
}: {
  ink: DraftInk;
  size?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={[DRAFT_WELL, size, draftInkSurface(ink), className ?? ""].join(
        " "
      )}
    >
      {children}
    </div>
  );
}

/**
 * Crop marks on the sheet that represents current state, so it reads as the
 * drawing being worked on rather than another page of history.
 *
 * The marks are drawn a pixel outside the sheet, so they land on whatever is
 * behind it rather than on the sheet itself. On grid paper the navy hairline is
 * right; over a dark surface it disappears, so callers that float a sheet over
 * one pass tone="light" and get the same marks in white.
 */
export function DraftCornerTicks({
  tone = "navy"
}: {
  tone?: "navy" | "light";
}) {
  const tick = [
    "pointer-events-none absolute size-2",
    tone === "light" ? "border-white/90" : "border-accent-line"
  ].join(" ");
  return (
    <>
      <span
        aria-hidden
        className={`${tick} -left-px -top-px border-l border-t`}
      />
      <span
        aria-hidden
        className={`${tick} -right-px -top-px border-r border-t`}
      />
      <span
        aria-hidden
        className={`${tick} -bottom-px -left-px border-b border-l`}
      />
      <span
        aria-hidden
        className={`${tick} -bottom-px -right-px border-b border-r`}
      />
    </>
  );
}
